import { getNamedFilter } from './DefaultFilters';
import { IsSmartSendByType } from './SmartSendSummaryGenerator';
import { SmartSendFilterContext, checkLists, checkArrayLists, isInTransit } from './SmartSendFilterApplier';
import { onlyUniqueNonBlank } from './ArrayHelper';
import { MarketplaceService } from '../consts/ItemMetaConstants';
import GenerateOptions from './SmartSendBulkActionOptions';
import { ApplierOption } from './ApplierOption';

export function DefaultFilter(): SmartSendFilterContext {
  return {
    AvailableFilters: [
      {
        name: 'MarketplaceSource',
        label: 'Marketplace',
        options: [],
        style: 'IMAGE',
        valid: (i, f, s) => {
          return checkLists(s, i.Source.Id) || !!i.MarketplaceOrderLines.find((m) => checkLists(s, m.Source.Id));
        }
      },
      {
        hidden: true,
        name: 'MarketplaceStatus',
        label: 'Marketplace Status',
        options: [
          { text: 'Sold', value: 'Sold' },
          { text: 'Notify Marketplace', value: 'AwaitingDispatch' },
          { text: 'Dispatched', value: 'Dispatched' }
        ],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.MarketplaceStatus)
      },
      {
        name: 'Status',
        label: 'Item Status',
        // hidden: true,
        options: [
          { text: 'Active', value: 'Active' },
          { text: 'Saved for Later', value: 'Inactive' }
        ],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Status)
      },
      {
        name: 'Sku',
        label: 'Product SKU',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(s, i.MarketplaceOrderLines.map((x) => x.Product.Sku).filter(onlyUniqueNonBlank))
      },
      {
        name: 'Issues',
        label: 'Product Issues',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => {
          if (s?.Include?.length > 0) {
            const o = GenerateOptions(i, i.State?.validation);
            if (!s.Include.some((x) => o.includes(x as ApplierOption))) {
              return false;
            }
          }
          return true;
        }
      },
      {
        name: 'MarketplaceService',
        label: 'Marketplace Service',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(
            s,
            i.MarketplaceOrderLines.map((x) => x.Meta[MarketplaceService]?.Value).filter(onlyUniqueNonBlank)
          )
      },
      {
        name: 'ProductName',
        label: 'Product',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(
            s,
            i.MarketplaceOrderLines.map((x) => x.Product?.Description)
          )
      },
      {
        name: 'TrackingStatus',
        label: 'Order Status',
        hidden: true,
        options: [
          { text: 'InTransit', value: 'InTransit' },
          { text: 'Delivered', value: 'Delivered' },
          { text: 'Not Booked', value: 'NotBooked' }
        ],
        style: 'CHECKBOX',
        valid: (i, f, s) => {
          if (!s) {
            return true;
          }

          if (!s.Include.length && !s.Exclude.length) {
            return true;
          }

          // Exc
          if (s.Exclude.includes('NotBooked') && i.MarketplaceStatus === 'Sold') {
            return false;
          }
          if (s.Exclude.includes('Delivered') && i.TrackingSummary?.LatestStatus === 'Delivered') {
            return false;
          }
          if (s.Exclude.includes('InTransit') && isInTransit(i)) {
            return false;
          }

          // Inc
          if (s.Include.includes('NotBooked') && i.MarketplaceStatus === 'Sold') {
            return true;
          }
          if (s.Include.includes('Delivered') && i.TrackingSummary?.LatestStatus === 'Delivered') {
            return true;
          }
          if (s.Include.includes('InTransit') && isInTransit(i)) {
            return true;
          }

          return false;
        }
      },
      {
        name: 'Service',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Service?.Id)
      },
      {
        name: 'Type',
        hidden: true,
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Type)
      },
      {
        name: 'SubType',
        hidden: true,
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.SubType)
      },
      {
        name: 'Account',
        hidden: false,
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(
            s,
            i.MarketplaceOrderLines.map((m) => m.Source.AccountId)
          )
      },
      {
        name: 'ServiceProvider',
        label: 'Courier Account',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Service?.Provider)
      },
      {
        name: 'Country',
        options: [],
        style: 'ICONCHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Recipient?.CountryIsoCode)
      },
      {
        name: 'Tag',
        label: 'Tags',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkArrayLists(s, i.Tags)
      },
      {
        name: 'Owner',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(
            s,
            i.Owners.map((x) => x.Id)
          )
      },
      {
        name: 'Extra',
        label: 'Added Extras',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) =>
          checkArrayLists(
            s,
            i.Extras.map((x) => x.Id)
          )
      },
      {
        name: 'Date',
        label: 'Order Date',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, new Date(i.Created).toDateString())
      },
      {
        name: 'Type',
        label: 'Order Type',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.Type)
      },
      {
        name: 'OrderQuantity',
        label: 'Quantity Ordered',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.MarketplaceOrderLines.length.toString())
      },
      {
        name: 'Weight',
        label: 'Parcel Weight',
        options: [],
        style: 'CHECKBOX',
        valid: (i, f, s) => checkLists(s, i.PackageDimensions.Weight?.toString())
      },
      {
        name: 'Error',
        label: 'Errors',
        hidden: true,
        options: [
          {
            text: 'Ready To book',
            value: 'READY'
          },
          {
            text: 'Address Issues',
            value: 'ADDRESS'
          },
          {
            text: 'Item Issues',
            value: 'ITEM'
          },
          {
            text: 'Service Issues',
            value: 'SERVICE'
          }
        ],
        style: 'CHECKBOX',
        valid: (i, f, s, c) => {
          if (s?.Include?.includes('Ready')) {
            if (
              !c?.validation?.Validated ||
              i.Status !== 'Active' ||
              i.MarketplaceStatus !== 'Sold' ||
              !IsSmartSendByType(i.Type)
            ) {
              return false;
            }
          }
          if (s?.Exclude?.includes('Ready')) {
            if (
              c?.validation?.Validated ||
              i.Status !== 'Active' ||
              i.MarketplaceStatus !== 'Sold' ||
              !IsSmartSendByType(i.Type)
            ) {
              return false;
            }
          }
          return true;
        }
      }
    ],
    CurrentFilter: getNamedFilter('DEFAULT')
  };
}
